import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {BreadcrumbParent} from "../breadcrumb/breadcrumb/breadcrumb.component";
import {trigger, transition, style, animate} from '@angular/animations';

@Component({
  selector: 'app-secondary-toolbar',
  templateUrl: './secondary-toolbar.component.html',
  styleUrls: ['./secondary-toolbar.component.scss'],
  animations: [
    trigger('buttonFadeScale', [
      // When a button enters the DOM
      transition(':enter', [
        style({
          opacity: 0,
          width: '0px',
          transform: 'scale(0.8)',
          overflow: 'hidden',
          whiteSpace: 'nowrap'
        }),
        animate('200ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({
          opacity: 1,
          width: '*',
          transform: 'scale(1)'
        }))
      ]),
      // When a button leaves the DOM
      transition(':leave', [
        style({
          overflow: 'hidden',
          whiteSpace: 'nowrap'
        }),
        animate('150ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({
          opacity: 0,
          width: '0px',
          transform: 'scale(0.8)'
        }))
      ])
    ])
  ]
})
export class SecondaryToolbarComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  // Allows the parent component to pass in a title string
  @Input() title: string = '';
  @Input() parents: BreadcrumbParent[] = [];
  @Input() self: string = '';
  @Input() actions: ToolbarAction[] = [];

  @ViewChild('toolbarContainer') toolbarContainer!: ElementRef;
  @ViewChild('measurementLayer') measurementLayer!: ElementRef;

  compactSidebar: boolean = document.querySelector('body')!.classList.contains('sidebar-compact');

  visibleActions: ToolbarAction[] = [];
  hiddenActions: ToolbarAction[] = [];

  private sortedActionsWithWidths: CachedActionItem[] = [];
  private lastVisibleCount = -1; // Cache the last count

  private resizeObserver: ResizeObserver;

  // Magic numbers for our layout math
  private readonly GAP_WIDTH = 8;
  private readonly MORE_BTN_WIDTH = 40 + this.GAP_WIDTH; // 40px icon button + 8px gap
  private readonly SAFE_BREADCRUMB_WIDTH = 180; // Minimum pixels we want to reserve for breadcrumbs

  private animationFrameId!: number;

  constructor(private cdr: ChangeDetectorRef) {
  }

  ngOnInit() {
    // Default to showing everything before the view initializes
    this.visibleActions = [...this.actions];
  }

  // 1. Hook into changes in case actions are loaded via API
  ngOnChanges(changes: SimpleChanges) {
    if (changes['actions'] && !changes['actions'].firstChange) {
      // Wait one tick for Angular to render the new invisible buttons, then measure
      setTimeout(() => {
        this.initializeObserver();
      });
    }
  }

  ngAfterViewInit() {
// Cast document to 'any' to bypass TypeScript strict type checking
    const doc = document as any;

    // Check if the browser actually supports the fonts API
    if (doc.fonts && doc.fonts.ready) {
      doc.fonts.ready.then(() => {
        setTimeout(() => {
          this.initializeObserver();
        });
      });
    } else {
      // Fallback for older browsers: just wait a raw 100ms for painting
      setTimeout(() => {
        this.initializeObserver();
      }, 100);
    }
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }


  private initializeObserver() {
    this.measureButtonWidths();

    // If the observer isn't set up yet, create it
    if (!this.resizeObserver && this.toolbarContainer) {
      this.resizeObserver = new ResizeObserver((entries) => {
        if (this.animationFrameId) {
          cancelAnimationFrame(this.animationFrameId);
        }

        this.animationFrameId = requestAnimationFrame(() => {
          for (const entry of entries) {
            this.calculateFit(entry.contentRect.width);
          }
        });
      });
      this.resizeObserver.observe(this.toolbarContainer.nativeElement);
    } else if (this.toolbarContainer) {
      // If the observer already exists (e.g., from ngOnChanges), just force a recalculation
      this.calculateFit(this.toolbarContainer.nativeElement.offsetWidth);
    }
  }

  private measureButtonWidths() {
    if (!this.measurementLayer || !this.actions) return;

    const buttons = this.measurementLayer.nativeElement.children;

    // Map the raw actions to include their physical DOM width and original index
    const mappedItems: CachedActionItem[] = this.actions.map((action, index) => {
      const DOMButton = buttons[index];
      const width = DOMButton ? DOMButton.offsetWidth + this.GAP_WIDTH : 0;

      return {
        action,
        width,
        originalIndex: index,
        priority: action.priority || 0
      };
    });

    // Sort by priority DESC immediately and store it in our permanent cache variable
    this.sortedActionsWithWidths = [...mappedItems].sort((a, b) => b.priority - a.priority);
  }

  private calculateFit(toolbarWidth: number) {
    if (!this.sortedActionsWithWidths || this.sortedActionsWithWidths.length === 0) return;
    if (!this.actions || this.actions.length === 0) {
      return;
    }
    // How much space do we actually have for buttons?
    const availableWidth = toolbarWidth - this.SAFE_BREADCRUMB_WIDTH;


    const assignedVisible: CachedActionItem[] = [];
    const assignedHidden: CachedActionItem[] = [];
    let currentWidthSum = 0;

    // A lightweight loop running against our pre-sorted cache
    for (let i = 0; i < this.sortedActionsWithWidths.length; i++) {
      const item = this.sortedActionsWithWidths[i];
      const isLastItem = i === this.sortedActionsWithWidths.length - 1;
      const moreBtnSpace = isLastItem ? 0 : this.MORE_BTN_WIDTH;

      if (currentWidthSum + item.width + moreBtnSpace <= availableWidth) {
        currentWidthSum += item.width;
        assignedVisible.push(item);
      } else {
        // Everything remaining in our priority list must be hidden
        assignedHidden.push(...this.sortedActionsWithWidths.slice(i));
        break;
      }
    }

    // Performance Guard remains intact
    if (assignedVisible.length === this.lastVisibleCount) {
      return;
    }
    this.lastVisibleCount = assignedVisible.length;

    // Re-sort the final buckets back to original visual order for display
    this.visibleActions = assignedVisible
      .sort((a, b) => a.originalIndex - b.originalIndex)
      .map(item => item.action);

    this.hiddenActions = assignedHidden
      .sort((a, b) => a.originalIndex - b.originalIndex)
      .map(item => item.action);

    this.cdr.detectChanges();
  }
}

export interface ToolbarAction {
  titleStringKey: string;
  iconClass: string;
  action: () => void;
  priority?: number;
}

interface CachedActionItem {
  action: any;
  width: number;
  originalIndex: number;
  priority: number;
}
